/**
 * OAuth Callback Page
 * Handles OAuth redirects from Meta Ads, Google/BigQuery, and other OAuth providers
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  SystemCardShell,
  SystemCardHeading,
  SystemCardActions,
  SystemCardPrimary,
  SystemCardSecondary,
  SystemCardProgress,
} from '@/components/shared/layout';
import { getConnectorErrorMessage } from '@/lib/utils/connectorErrors';
import { track } from '@/lib/utils/track';

type CallbackState = 'loading' | 'success' | 'error';
type OAuthProvider = 'meta' | 'google' | 'google-ads' | 'gsc' | 'unknown';

const PROVIDER_NAMES: Record<OAuthProvider, string> = {
  meta: 'Meta Ads',
  google: 'Google',
  'google-ads': 'Google Ads',
  gsc: 'Google Search Console',
  unknown: 'Your account',
};

function parseProvider(value: string | null): OAuthProvider {
  const normalized = value?.replace('_', '-');
  return normalized && normalized in PROVIDER_NAMES ? (normalized as OAuthProvider) : 'unknown';
}

/**
 * Map raw OAuth error strings from the callback URL to user-friendly messages.
 * Falls back to the connector error utility for pattern matching.
 */
function getOAuthErrorMessage(rawError: string): string {
  // Try the connector error utility first (handles known error codes and patterns)
  const mapped = getConnectorErrorMessage(rawError);
  if (mapped !== rawError && mapped !== 'An unexpected error occurred. Please try again.') {
    return mapped;
  }

  // Fallback: if the raw error is short and readable, display it; otherwise use generic
  if (rawError.length < 120 && rawError.includes(' ') && !rawError.includes('HTTP') && !rawError.includes('stack')) {
    return rawError;
  }

  return 'Something went wrong during sign-in with the provider. Try again.';
}

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>('loading');
  const [message, setMessage] = useState<string>('');
  const [provider, setProvider] = useState<OAuthProvider>('unknown');

  useEffect(() => {
    const success = searchParams.get('success');
    const connId = searchParams.get('connectionId');
    const error = searchParams.get('error');
    const oauthProvider = parseProvider(searchParams.get('provider'));

    setProvider(oauthProvider);

    if (success === 'true' && connId) {
      setState('success');
      track('connector_oauth_completed', null, { connector_type: oauthProvider, success: true });
    } else if (error) {
      setState('error');
      setMessage(getOAuthErrorMessage(error));
      track('connector_oauth_completed', null, { connector_type: oauthProvider, success: false });
    } else {
      setState('error');
      setMessage('The provider sent us back without a result. Try connecting again.');
    }
  }, [searchParams]);

  const handleContinue = () => {
    // Check if we're returning from onboarding
    const onboardingReturn = sessionStorage.getItem('onboarding_return');
    if (onboardingReturn) {
      try {
        const data = JSON.parse(onboardingReturn);
        sessionStorage.removeItem('onboarding_return');

        // Return to onboarding at the setup step
        const params = new URLSearchParams({
          workspaceId: data.workspaceId,
          step: 'setup',
        });
        navigate(`/onboarding/workspace?${params.toString()}`);
        return;
      } catch (error) {
        console.error('Failed to parse onboarding return data:', error);
      }
    }

    // Use saved return path if available
    const returnPath = sessionStorage.getItem('oauth_return_path');
    if (returnPath) {
      sessionStorage.removeItem('oauth_return_path');
      navigate(returnPath);
      return;
    }

    navigate('/dashboard');
  };

  const handleRetry = () => {
    const returnPath = sessionStorage.getItem('oauth_return_path');
    if (returnPath) {
      sessionStorage.removeItem('oauth_return_path');
      navigate(returnPath);
      return;
    }
    navigate('/dashboard');
  };

  const name = PROVIDER_NAMES[provider];
  const eyebrow = provider === 'unknown' ? 'Connector' : name;
  const isAds = provider === 'meta' || provider === 'google-ads';

  return (
    <SystemCardShell>
      {state === 'loading' && (
        <>
          <SystemCardHeading eyebrow={eyebrow} title="Finishing the connection">
            {provider === 'unknown' ? 'Saving access to your account.' : `Saving access to your ${name} account.`}
          </SystemCardHeading>
          <SystemCardProgress label="Connecting" />
        </>
      )}

      {state === 'error' && (
        <>
          <SystemCardHeading
            eyebrow={eyebrow}
            title={provider === 'unknown' ? "The connection didn't finish" : `${name} didn't connect`}
          >
            {message}
          </SystemCardHeading>
          <SystemCardActions>
            <SystemCardPrimary onClick={handleRetry}>Try again</SystemCardPrimary>
            <SystemCardSecondary onClick={handleContinue}>Back to connectors</SystemCardSecondary>
          </SystemCardActions>
        </>
      )}

      {state === 'success' && (
        <>
          <SystemCardHeading
            success
            eyebrow={eyebrow}
            title={provider === 'unknown' ? "You're connected" : `${name} is connected`}
          >
            {isAds ? 'Spend and campaigns start syncing now.' : 'Data starts syncing now.'} The first sync can take a few
            minutes.
          </SystemCardHeading>
          <SystemCardSecondary onClick={handleContinue}>Back to connectors</SystemCardSecondary>
        </>
      )}
    </SystemCardShell>
  );
}
